'use client';
import { add, larger, largerEq, number, smaller, smallerEq, subtract } from 'mathjs';
import React, { useEffect, useMemo, useState } from 'react';
import { textAlignMap } from './constants';

interface IProps {
  value?: number | string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  maxValue?: string;
  minValue?: string;
  className?: string;
  textAlign?: 'start' | 'center' | 'end';
  disabled?: boolean;
  preventDefault?: boolean;
}

export const InputNumber = (props: IProps) => {
  const [value, setValue] = useState<string>(props.value?.toString());
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    value = value.replace(/[^0-9]/, '');
    if (!value) {
      changeValue(value);
      return;
    }
    if (props.maxValue && larger(number(value), number(props.maxValue))) {
      changeValue(props.maxValue);
      return;
    }
    if (props.minValue && smaller(number(value), number(props.minValue))) {
      return;
    }
    changeValue(value);
  };

  const changeValue = (value: string) => {
    if (!props?.preventDefault) {
      setValue(value);
    }
    props.onChange && props.onChange(value);
  };

  useEffect(() => {
    if (value !== props.value) {
      setValue(props.value.toString());
    }
  }, [props.value]);

  const onSubtract = () => {
    const newValue = subtract(number(value ?? 0), 1);
    if (props.minValue && smaller(newValue, number(props.minValue))) {
      return;
    }
    changeValue(newValue.toString());
  };

  const onBlur = () => {
    props.onBlur && props.onBlur();
  };

  const onAdd = () => {
    const newValue = add(number(value ?? 0), 1);
    if (props.maxValue && larger(newValue, number(props.maxValue))) {
      return;
    }
    changeValue(newValue.toString());
  };

  const subtractButtonDisable = useMemo(() => {
    if (!props.minValue) return false;
    return smallerEq(number(value ?? 0), number(props.minValue)) as boolean;
  }, [value, props.minValue]);

  const addButtonDisable = useMemo(() => {
    if (!props.maxValue) return false;
    return largerEq(number(value ?? 0), number(props.maxValue)) as boolean;
  }, [props.maxValue, value]);

  return (
    <div className={`flex h-10 rounded border border-solid border-gray-450 overflow-hidden ${props.className ?? ''}`}>
      <button
        className={`w-8 bg-white cursor-pointer border-0 flex-shrink-0 flex items-center justify-center disabled:cursor-not-allowed custom-counter-reduce`}
        onClick={onSubtract}
        disabled={subtractButtonDisable || props.disabled}
      >
        <i className="icon-16-minus-mono" />
      </button>
      <input
        className={`rounded-none border-0 border-x outline-none border-solid border-gray-450 flex-1 w-0 ${
          props.textAlign ? textAlignMap[props.textAlign] : 'text-start'
        }`}
        disabled={props.disabled}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
      />
      <button
        className="w-8 bg-white cursor-pointer border-0 flex-shrink-0 flex items-center justify-center disabled:cursor-not-allowed custom-counter-add"
        onClick={onAdd}
        disabled={addButtonDisable || props.disabled}
      >
        <i className="icon-16-plus-mono" />
      </button>
    </div>
  );
};
